import React, { useState, useEffect, useRef } from 'react';
import CreditOffersService from 'services/credit-offers/credit-offers-service';
import { AdaptedCreditOffersResponse, CreditOfferTypes } from 'types/CreditOfferTypes';
import { userHasNonCreditAccount, userIsBnplEligible } from 'utilities/creditOffers/creditOfferUtilities';
import { SiteName } from 'types/Site';
import { useSelectedSkuContext } from 'context/SelectedSku/SelectedSkuContext';
import BrandTypes from 'constants/brand';
import { ProductVariant } from 'types/Product';
import SkeletonCard from './Card/SkeletonCard';
import CreditToAccountCard from './Card/CreditToAccountCard';
import CreditToAccountDialog from './Dialog/CreditToAccountDialog';
import YouCouldPayCard from './Card/YouCouldPayCard';
import YouCouldPayDialog from './Dialog/YouCouldPayDialog';

type Props = {
  productName: string;
  productPriceDecimal: number;
  itemNumber: string;
  site: SiteName;
  skus: ProductVariant[];
  creditOffersMockEnabled?: boolean;
};

function CreditOffers(props: Props) {
  const { productName, productPriceDecimal, itemNumber, site, skus, creditOffersMockEnabled } = props;
  const { selectedSku } = useSelectedSkuContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nonCreditAccountUser, setNonCreditAccountUser] = useState(false);
  const [dialogRendered, setDialogRendered] = useState(false);
  const [creditOffers, setCreditOffers] = useState<AdaptedCreditOffersResponse | void>();
  const [loading, setLoading] = useState(true);
  const isFirstRender = useRef(true);
  const creditOffersService = new CreditOffersService(creditOffersMockEnabled);

  const getCreditOffers = async () => {
    const isBnplEligible = userIsBnplEligible();
    const response = await creditOffersService.getCreditOffers(
      {
        price: productPriceDecimal,
        catalogueId: itemNumber,
        isBnplEligible,
      },
      creditOffersMockEnabled
        ? {
            headers: { 'x-mock-response': `${site}-BNPL` },
          }
        : {},
    );

    setCreditOffers(response);
    setLoading(false);
  };

  useEffect(() => {
    getCreditOffers();
    setNonCreditAccountUser(userHasNonCreditAccount);
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (dialogRendered) {
      setDialogOpen(true);
    }
  }, [dialogRendered]);

  const openDialog = () => {
    if (dialogRendered) {
      setDialogOpen(true);
    } else {
      setDialogRendered(true);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  if (loading) return <SkeletonCard site={site} />;

  if (creditOffers?.primary?.type === CreditOfferTypes.CTA) {
    const { apr, primary } = creditOffers;

    return (
      <>
        <CreditToAccountCard
          apr={apr}
          voucherCode={primary.attributes.voucherCode}
          offerTitle={primary.attributes.offerTitle}
          endDate={primary.attributes.endDate}
          termLength={primary.attributes.termLength}
          onClickCallback={openDialog}
          nonCreditAccountUser={nonCreditAccountUser}
          site={site}
        />

        {dialogRendered && (
          <CreditToAccountDialog
            dialogTitle={primary.attributes.offerTitle}
            voucherCode={primary.attributes.voucherCode}
            startDate={primary.attributes.startDate}
            endDate={primary.attributes.endDate}
            offerTerms1={primary.attributes.offerTerms1}
            offerTerms2={primary.attributes.offerTerms2}
            offerTerms3={primary.attributes.offerTerms3}
            offerTerms4={primary.attributes.offerTerms4}
            onClose={closeDialog}
            open={dialogOpen}
            site={site}
          />
        )}
      </>
    );
  }

  if (site === BrandTypes.LITTLEWOODS) {
    const hasBNPL = creditOffers?.primary?.type === CreditOfferTypes.BNPL;
    const hasPriceTerms = skus.every((sku) => sku.price?.priceTerms);
    const has208WeeklyPriceTerm = skus.some((sku) => sku.price?.has208WeeklyPriceTerm);
    const hasSelectedSku = !!selectedSku;
    const hasLongestWeeklyPriceTerm = !!selectedSku?.price?.longestWeeklyPriceTermWithZeroInterest;

    let creditOfferType;

    if (!has208WeeklyPriceTerm && hasBNPL) {
      creditOfferType = CreditOfferTypes.BNPL;
    } else if (hasLongestWeeklyPriceTerm) {
      creditOfferType = CreditOfferTypes.STC;
    } else if (hasPriceTerms && !hasSelectedSku) {
      creditOfferType = CreditOfferTypes.STC_NO_OPTION_SELECTED;
    }

    if (!creditOfferType) return null;

    return (
      <>
        <YouCouldPayCard
          creditOfferType={creditOfferType}
          longestWeeklyPriceTermWithZeroInterest={selectedSku?.price?.longestWeeklyPriceTermWithZeroInterest}
          onClickCallback={openDialog}
        />

        {dialogRendered && (
          <YouCouldPayDialog
            open={dialogOpen}
            priceTerms={selectedSku?.price?.priceTerms}
            productName={productName}
            longestWeeklyPriceTermWithInterest={selectedSku?.price?.longestWeeklyPriceTermWithInterest}
            longestWeeklyPriceTermWithZeroInterest={selectedSku?.price?.longestWeeklyPriceTermWithZeroInterest}
            selectedSkuPrice={selectedSku?.price?.amount.current}
            showBNPL={hasBNPL}
            onClose={closeDialog}
          />
        )}
      </>
    );
  }

  return null;
}

export default CreditOffers;
